import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Download,
  X,
  Star,
  Phone,
  Mail,
  FileText,
  ShieldCheck,
  Award,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelVendorManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [detailVendor, setDetailVendor] = useState(null);
  const [deleteVendor, setDeleteVendor] = useState(null);

  // Form State
  const initialForm = {
    vendor_name: '',
    vendor_type: 'airline',
    contact_person: '',
    phone: '',
    email: '',
    gst_number: '',
    pan_number: '',
    address: 'Corporate Park, Bengaluru',
    agreement_valid_till: '2027-12-31',
    payment_terms: '30 Days Credit',
    sla: 'Guaranteed 2-Hour Ticket Issuance SLA',
    rating: 5,
    status: 'active',
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/vendors`, { withCredentials: true });
      setVendors(data.vendors || []);
    } catch (err) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    if (!formData.vendor_name || !formData.vendor_type) {
      toast.error('Please enter vendor name and type');
      return;
    }

    try {
      if (editVendor) {
        await axios.put(`${API_URL}/travel-desk/vendors/${editVendor._id}`, formData, { withCredentials: true });
        toast.success('Vendor details updated successfully!');
      } else {
        await axios.post(`${API_URL}/travel-desk/vendors`, formData, { withCredentials: true });
        toast.success('New Travel Vendor added successfully!');
      }
      setShowCreateModal(false);
      setEditVendor(null);
      setFormData(initialForm);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/vendors/${vendorId}`, { withCredentials: true });
      toast.success('Vendor deleted successfully');
      setDeleteVendor(null);
      fetchVendors();
    } catch (err) {
      toast.error('Failed to delete vendor');
    }
  };

  const openEdit = (v) => {
    setEditVendor(v);
    setFormData({
      vendor_name: v.vendor_name || '',
      vendor_type: v.vendor_type || 'airline',
      contact_person: v.contact_person || '',
      phone: v.phone || '',
      email: v.email || '',
      gst_number: v.gst_number || '',
      pan_number: v.pan_number || '',
      address: v.address || '',
      agreement_valid_till: v.agreement_valid_till || '',
      payment_terms: v.payment_terms || '30 Days Credit',
      sla: v.sla || 'Guaranteed SLA',
      rating: v.rating || 5,
      status: v.status || 'active',
    });
    setShowCreateModal(true);
  };

  const exportCSV = () => {
    const headers = ['Vendor Name', 'Type', 'Contact Person', 'Phone', 'Email', 'GST', 'PAN', 'Payment Terms', 'Rating', 'Status'];
    const rows = filteredVendors.map(v => [
      `"${v.vendor_name}"`, v.vendor_type, v.contact_person, v.phone,
      v.email, v.gst_number, v.pan_number, `"${v.payment_terms}"`, `${v.rating || 5} Stars`, v.status
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Travel_Vendors_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredVendors = vendors.filter((v) => {
    const q = searchTerm.toLowerCase();
    const matchType = vendorTypeFilter === 'all' || v.vendor_type === vendorTypeFilter;
    const matchQuery = (
      (v.vendor_name || '').toLowerCase().includes(q) ||
      (v.contact_person || '').toLowerCase().includes(q) ||
      (v.phone || '').toLowerCase().includes(q) ||
      (v.gst_number || '').toLowerCase().includes(q)
    );
    return matchType && matchQuery;
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <Building2 className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Travel Vendor Management</h1>
            <p className="text-xs text-blue-200">
              Airlines, IRCTC, Cab Operators, Hotel Chains, SLAs &amp; Corporate Agreements.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={exportCSV}
            className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>

          {hasPermission(user, 'travel_desk.vendors.edit') && (
            <button
              onClick={() => {
                setEditVendor(null);
                setFormData(initialForm);
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Create Vendor
            </button>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search vendor name, GST, contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold">
            <Filter className="w-3.5 h-3.5" /> Category:
          </div>
          <select
            value={vendorTypeFilter}
            onChange={(e) => setVendorTypeFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Vendor Categories</option>
            <option value="airline">Airline</option>
            <option value="hotel">Hotel Chain</option>
            <option value="cab_vendor">Cab Operator</option>
            <option value="irctc">IRCTC / Train</option>
            <option value="bus">Bus Operator</option>
            <option value="travel_agency">Travel Agency</option>
          </select>

          <button
            onClick={fetchVendors}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex justify-between">
          <span>Empanelled Vendor Directory ({filteredVendors.length})</span>
          <span className="text-slate-400">Live Database Master</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading vendor list...</div>
        ) : filteredVendors.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">
            No vendors found matching your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3.5">Vendor Name</th>
                  <th className="p-3.5">Category Type</th>
                  <th className="p-3.5">Contact Person</th>
                  <th className="p-3.5">Phone / Email</th>
                  <th className="p-3.5">GST Number</th>
                  <th className="p-3.5">Payment Terms</th>
                  <th className="p-3.5">Rating</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {filteredVendors.map((v) => (
                  <tr key={v._id} className="hover:bg-slate-50">
                    <td className="p-3.5 font-bold text-slate-900">{v.vendor_name}</td>
                    <td className="p-3.5 font-bold text-blue-600 uppercase text-[11px]">{v.vendor_type}</td>
                    <td className="p-3.5">{v.contact_person || 'N/A'}</td>
                    <td className="p-3.5 font-mono">
                      <div>{v.phone}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{v.email}</div>
                    </td>
                    <td className="p-3.5 font-mono text-slate-600">{v.gst_number || 'N/A'}</td>
                    <td className="p-3.5">{v.payment_terms}</td>
                    <td className="p-3.5">
                      <div className="flex items-center text-amber-500 font-bold">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        <span className="ml-1 text-slate-800">{v.rating || 5}.0</span>
                      </div>
                    </td>
                    <td className="p-3.5">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        v.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="p-3.5 text-right space-x-1 shrink-0">
                      <button
                        onClick={() => setDetailVendor(v)}
                        className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-bold text-[11px]"
                        title="Contracts & SLA Details"
                      >
                        SLA / Docs
                      </button>
                      {hasPermission(user, 'travel_desk.vendors.edit') && (
                        <button
                          onClick={() => openEdit(v)}
                          className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg font-bold text-[11px]"
                          title="Edit Vendor"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteVendor(v)}
                        className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg font-bold text-[11px]"
                        title="Delete Vendor"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" />
                {editVendor ? `Edit Vendor (${editVendor.vendor_name})` : 'Empanel New Travel Vendor'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 font-bold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveVendor} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Vendor Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="IndiGo Airlines / Ola Corporate"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Vendor Category *</label>
                  <select
                    value={formData.vendor_type}
                    onChange={(e) => setFormData({ ...formData, vendor_type: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-bold"
                  >
                    <option value="airline">Airline</option>
                    <option value="hotel">Hotel Chain</option>
                    <option value="cab_vendor">Cab Operator</option>
                    <option value="irctc">IRCTC / Rail</option>
                    <option value="bus">Bus Operator</option>
                    <option value="travel_agency">Travel Agency</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="Key Account Manager"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Mobile / Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="+91 9876543210"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="corporate@vendor.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                    placeholder="29AAACI0001A1Z1"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">PAN Number</label>
                  <input
                    type="text"
                    value={formData.pan_number}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                    placeholder="AAACI0001A"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Payment Terms</label>
                  <input
                    type="text"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="30 Days Credit / Pre-paid"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">SLA Agreement Terms</label>
                <input
                  type="text"
                  value={formData.sla}
                  onChange={(e) => setFormData({ ...formData, sla: e.target.value })}
                  className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  placeholder="Guaranteed 2-Hour Ticket Issuance SLA"
                />
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md"
                >
                  {editVendor ? 'Save Changes' : 'Empanel Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SLA & CONTRACT DETAILS DRAWER */}
      {detailVendor && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" /> Vendor Contract &amp; Performance SLA
              </h3>
              <button onClick={() => setDetailVendor(null)} className="text-slate-400 font-bold">&times;</button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-slate-50 p-4 rounded-xl space-y-1">
                <div className="text-sm font-black text-slate-900">{detailVendor.vendor_name}</div>
                <div className="text-blue-600 font-bold uppercase text-[10px]">{detailVendor.vendor_type}</div>
              </div>

              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-400 font-medium block">Contact Person</span>
                  <span className="font-bold text-slate-900">{detailVendor.contact_person || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Phone</span>
                  <span className="font-mono font-bold text-slate-900">{detailVendor.phone || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">GST Number</span>
                  <span className="font-mono font-bold text-slate-900">{detailVendor.gst_number || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Payment Terms</span>
                  <span className="font-bold text-slate-900">{detailVendor.payment_terms}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl space-y-1">
                <span className="text-amber-900 font-bold">Service Level Agreement (SLA)</span>
                <p className="text-slate-700 text-[11px]">{detailVendor.sla || 'Standard SLA active.'}</p>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button onClick={() => setDetailVendor(null)} className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteVendor && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-rose-600 text-base">Confirm Delete</h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete vendor <b>{deleteVendor.vendor_name}</b>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleteVendor(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Cancel
              </button>
              <button onClick={() => handleDeleteVendor(deleteVendor._id)} className="px-5 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl">
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelVendorManagement;
